// bluaIQ — Diagnostic Takeoff v3 — DiagnosticArtifactList
// Read-only listing of diagnostic artifacts. Treats every artifact as
// candidate-only; never displays it as a measurement, quantity, or
// final answer.

import { Boxes, FileText, AlertTriangle } from 'lucide-react'
import type { DiagnosticTakeoffArtifactManifest } from '../../takeoff-diagnostic-v3/types'

export interface DiagnosticArtifactListProps {
  artifacts: DiagnosticTakeoffArtifactManifest[]
}

const KIND_LABEL: Record<string, string> = {
  source_identity: 'Source identity',
  safety_flags: 'Safety flags',
  source_review_ui: 'Source review UI',
  blocker_registry: 'Blocker registry',
  confidence_registry: 'Confidence registry',
  no_silent_drop: 'No-silent-drop',
  region_label_schema: 'Region label schema',
  region_annotation_record: 'Region annotation',
  audit_bundle: 'Audit bundle',
  evidence_engine_bridge: 'Evidence engine bridge',
  placeholder: 'Placeholder',
}

export default function DiagnosticArtifactList({ artifacts }: DiagnosticArtifactListProps) {
  if (artifacts.length === 0) {
    return (
      <div className="text-center py-10 border border-dashed border-[#ECEAE4] rounded-xl">
        <Boxes size={32} className="mx-auto text-[#ECEAE4] mb-2" />
        <p className="text-[12px] font-[700] text-[#9E9890]">No diagnostic artifacts yet</p>
        <p className="text-[10px] font-[600] text-[#9E9890] mt-1">
          Candidate-only. No accepted quantities are produced here.
        </p>
      </div>
    )
  }
  return (
    <ul className="space-y-2">
      {artifacts.map((a) => (
        <li
          key={a.id}
          className="bg-white border border-[#ECEAE4] rounded-xl p-3 flex items-start gap-3"
        >
          <FileText size={14} className="text-[#9E9890] mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] font-[800] text-[#1C1A17] truncate">
                {a.label}
              </span>
              <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] bg-[#F2F0EB] px-2 py-0.5 rounded-full">
                {KIND_LABEL[a.kind] ?? a.kind}
              </span>
              {!a.noSilentDropOk && (
                <span className="inline-flex items-center gap-1 text-[9px] font-[800] text-red-700 bg-red-50 border border-red-100 rounded-full px-2 py-0.5">
                  <AlertTriangle size={10} /> silent-drop suspected
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[9px] font-[800] text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5">
                diagnostic-only
              </span>
            </div>
            <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
              {a.payloadHash ? `sha256: ${a.payloadHash.slice(0, 16)}…` : 'no payload'}
              {' · '}
              {new Date(a.createdAt).toLocaleString()}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}
